import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import {
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {
  MatLegacyDialogRef as MatDialogRef,
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
} from '@angular/material/legacy-dialog';
import { MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import { SnackBarConfig } from 'v2/modules/shared/configs/snackbar.config';
import { IGetApiResponse } from 'v2/modules/shared/interfaces/api-response.interface';
import { IAppIntegrationAccount } from 'v2/modules/shared/interfaces/app-integration-account.interface';
import { PipeModule } from 'v2/modules/shared/pipes/pipe.module';
import { AppIntegrationAccountApiService } from 'v2/modules/shared/services/api/app-integration/app-integration-account-api.service';
import { SalesOrderApiService } from 'v2/modules/shared/services/api/sales-order-api.service';
import { LoadingDialogUtilService } from 'v2/modules/shared/services/util/loading-dialog-util.service';

import { trackById } from '../../../shared/util/track-by.util';

@Component({
  selector: 'lum-sales-order-pull-orders-form',
  templateUrl: './sales-order-pull-orders-form.component.html',
  styleUrls: ['./sales-order-pull-orders-form.component.scss'],
  standalone: true,
  imports: [CommonModule, MatSelectModule, ReactiveFormsModule, PipeModule],
})
export class SalesOrderPullOrdersFormComponent implements OnInit {
  form :UntypedFormGroup;
  private onDestroy$ = new Subject();
  appIntegrationAccounts :IAppIntegrationAccount[] = [];
  trackById = trackById;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data :any,
    private dialogRef :MatDialogRef<SalesOrderPullOrdersFormComponent>,
    private fb :UntypedFormBuilder,
    private snackbar :MatSnackBar,
    private appIntegrationAccountService :AppIntegrationAccountApiService,
    private salesOrderApiService :SalesOrderApiService,
    private loadingDialogService :LoadingDialogUtilService,
  ) {}

  ngOnInit() :void {
    this.getAppIntegrationAccounts();
    this.initForm();
  }

  onSubmit() :void {
    const appIntegrationAccountIds = this.form.value.appIntegrationAccountIds;

    if (!appIntegrationAccountIds.length) {
      this.dialogRef.close();
    }

    this.loadingDialogService.show('Pulling orders...');

    this.salesOrderApiService
      .pullOrders(appIntegrationAccountIds)
      .pipe(take(1))
      .subscribe({
        next: (response :any) => {
          this.loadingDialogService.close();
          this.snackbar.open(response.success, 'Success', {
            duration: SnackBarConfig.DEFAULT.duration,
          });

          this.dialogRef.close(true);
        },
        error: (response :any) => {
          this.loadingDialogService.close();
          this.snackbar.open(response.error, 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });

          this.dialogRef.close();
        },
      });
  }

  onClose() :void {
    this.dialogRef.close();
  }

  private getAppIntegrationAccounts() :void {
    const params :{ [key :string] :string } = {
      withPullOrdersChannelIntegrationMapping: 'true',
      limit: '100',
    };

    this.appIntegrationAccountService
      .get(params)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response :IGetApiResponse) => {
          this.appIntegrationAccounts = response.data;
        },
        error: (response :any) => {
          const message = response.error ?? 'Failed to pull products.';
          this.snackbar.open(message, 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  private initForm() :void {
    this.form = this.fb.group({
      appIntegrationAccountIds: [[], [Validators.required]],
    });
  }

  get selectedAppIntegrationAccounts() :string {
    const selectedIds = this.form.get('appIntegrationAccountIds')?.value;
    const selectedLabels = this.appIntegrationAccounts
      .filter((appIntegrationAccoun) =>
        selectedIds.includes(appIntegrationAccoun.id),
      )
      .map((appIntegrationAccount) => appIntegrationAccount.label);

    return selectedLabels.join(', ');
  }
}
